/**
 * Physical-keyboard PIN entry for the web build.
 *
 * The PIN screen is an on-screen keypad. On a desktop POS the cashier has a
 * real keyboard (or number pad) and expects to type the PIN; without this the
 * keys do nothing. Digits 0-9 and Backspace are forwarded to the same handler
 * the on-screen keys call, so the screen's own logic is unchanged.
 *
 * Ignored while the screen is not focused (it stays mounted under Forgot PIN),
 * while `enabled` is false (loader / inactivity screen), with a modifier held
 * (Ctrl+R, Ctrl+1 ... keep their browser meaning), and when the key is aimed
 * at a real text field.
 */

import {useEffect, useRef} from 'react';
import {useIsFocused} from '@react-navigation/native';

export function usePinKeyboard(
  onKey: (key: number | 'backspace') => void,
  enabled: boolean,
): void {
  const isFocused = useIsFocused();
  // Always call the latest handler - it closes over the current PIN state.
  const onKeyRef = useRef(onKey);
  onKeyRef.current = onKey;

  const active = enabled && isFocused;

  useEffect(() => {
    if (!active) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey || event.repeat) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }

      if (/^[0-9]$/.test(event.key)) {
        event.preventDefault();
        onKeyRef.current(Number(event.key));
      } else if (event.key === 'Backspace') {
        event.preventDefault();
        onKeyRef.current('backspace');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [active]);
}
